// ============================================================================
// CANCUN100 — ficha.js  (Bloque 28: ficha de agentes — datos, pasos, validaciones y archivos)
// ----------------------------------------------------------------------------
// Tabla agentes_ficha (PK agente_id) + columnas en agentes (ficha_solicitada_en/por, ficha_completa_en,
// foto_url, preferencias). Bucket PRIVADO `fichas-agentes`: ruta <agente_id>/<campo>.<ext>; en las columnas
// *_url y en agentes.foto_url se guarda la RUTA dentro del bucket y se lee SIEMPRE con URL firmada.
// Nada de SQL aquí: solo select / upsert / update / storage. Nunca .delete().
// Privacidad: CLABE, identificación y comprobantes solo los ve el agente y los admins con `puedeVerSensibles`.
// ============================================================================
import { supabase } from './supabaseClient';

export const BUCKET_FICHAS = 'fichas-agentes';
export const TIPOS_ID = [['INE', 'INE'], ['pasaporte', 'Pasaporte']];
export const TALLAS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
export const PARENTESCOS = ['Madre', 'Padre', 'Esposo(a)', 'Pareja', 'Hermano(a)', 'Hijo(a)', 'Amigo(a)', 'Otro'];
export const CAMPOS_SENSIBLES = ['banco', 'clabe', 'titular_cuenta', 'identificacion_tipo', 'identificacion_url', 'comprobante_domicilio_url', 'caratula_bancaria_url'];

// Los 13 pasos del flujo (un dato a la vez). `campos` = columnas de agentes_ficha que llena; foto → agentes.foto_url.
export const PASOS = [
  { n: 1, clave: 'foto', titulo: 'Foto de perfil', obligatorio: true, tipo: 'archivo', campos: [], archivo: 'foto', ayuda: 'Una foto clara de tu cara, de frente. Puedes tomarla con la cámara.' },
  { n: 2, clave: 'nombre', titulo: 'Nombre completo', obligatorio: true, campos: ['nombre_completo'] },
  { n: 3, clave: 'nacimiento', titulo: 'Fecha de nacimiento', obligatorio: true, campos: ['fecha_nacimiento'] },
  { n: 4, clave: 'celular', titulo: 'Celular', obligatorio: true, campos: ['celular'] },
  { n: 5, clave: 'correo', titulo: 'Correo personal', obligatorio: false, campos: ['correo_personal'] },
  { n: 6, clave: 'direccion', titulo: 'Dirección', obligatorio: true, campos: ['direccion'] },
  { n: 7, clave: 'emergencia', titulo: 'Contacto de emergencia', obligatorio: true, campos: ['emergencia_nombre', 'emergencia_telefono', 'emergencia_parentesco'] },
  { n: 8, clave: 'identificacion', titulo: 'Identificación', obligatorio: true, campos: ['identificacion_tipo'], archivo: 'identificacion_url', sensible: true, ayuda: 'INE o pasaporte: foto donde se lea bien.' },
  { n: 9, clave: 'comprobante', titulo: 'Comprobante de domicilio', obligatorio: false, tipo: 'archivo', campos: [], archivo: 'comprobante_domicilio_url', sensible: true, ayuda: 'Foto o PDF (luz, agua, teléfono…).' },
  { n: 10, clave: 'banco', titulo: 'Datos bancarios', obligatorio: true, campos: ['banco', 'clabe', 'titular_cuenta'], sensible: true, ayuda: 'Para pagarte tus comisiones. La CLABE tiene 18 dígitos.' },
  { n: 11, clave: 'caratula', titulo: 'Carátula bancaria', obligatorio: false, tipo: 'archivo', campos: [], archivo: 'caratula_bancaria_url', sensible: true, ayuda: 'Foto o PDF de la carátula del estado de cuenta.' },
  { n: 12, clave: 'talla', titulo: 'Talla de playera', obligatorio: false, campos: ['talla_playera'] },
  { n: 13, clave: 'revision', titulo: 'Revisión y envío', obligatorio: false, tipo: 'revision', campos: [] },
];
export const TOTAL_PASOS = PASOS.length;
export const ETIQUETA_CAMPO = {
  nombre_completo: 'Nombre completo', fecha_nacimiento: 'Fecha de nacimiento', celular: 'Celular', correo_personal: 'Correo personal', direccion: 'Dirección',
  emergencia_nombre: 'Nombre (emergencia)', emergencia_telefono: 'Teléfono (emergencia)', emergencia_parentesco: 'Parentesco',
  identificacion_tipo: 'Tipo de identificación', identificacion_url: 'Identificación', comprobante_domicilio_url: 'Comprobante de domicilio',
  banco: 'Banco', clabe: 'CLABE', titular_cuenta: 'Titular de la cuenta', caratula_bancaria_url: 'Carátula bancaria', talla_playera: 'Talla de playera', notas: 'Notas',
};

const lleno = (v) => v != null && String(v).trim() !== '';
export const soloDigitos = (s) => String(s || '').replace(/\D/g, '');
// CLABE: 18 dígitos y dígito verificador (pesos 3, 7, 1 sobre los primeros 17).
export const clabeValida = (s) => {
  const d = soloDigitos(s);
  if (d.length !== 18) return false;
  const pesos = [3, 7, 1];
  let suma = 0;
  for (let i = 0; i < 17; i++) suma += ((Number(d[i]) * pesos[i % 3]) % 10);
  return ((10 - (suma % 10)) % 10) === Number(d[17]);
};
export const correoValido = (c) => !lleno(c) || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(c).trim());
export const telefonoValido = (t) => soloDigitos(t).length >= 10;

// ¿Está completo el paso? (ficha = fila de agentes_ficha, agente = fila de agentes con foto_url)
export const pasoCompleto = (paso, ficha, agente) => {
  const f = ficha || {};
  if (paso.clave === 'revision') return !!f.completada_en;
  if (paso.archivo === 'foto') return lleno(agente && agente.foto_url);
  if (paso.tipo === 'archivo') return lleno(f[paso.archivo]);
  const camposOk = paso.campos.every((c) => lleno(f[c]));
  if (paso.archivo) return camposOk && lleno(f[paso.archivo]);
  return camposOk;
};
export const errorPaso = (paso, ficha) => {
  const f = ficha || {};
  if (paso.clave === 'celular' && lleno(f.celular) && !telefonoValido(f.celular)) return 'El celular debe tener al menos 10 dígitos.';
  if (paso.clave === 'correo' && !correoValido(f.correo_personal)) return 'El correo no tiene un formato válido.';
  if (paso.clave === 'emergencia' && lleno(f.emergencia_telefono) && !telefonoValido(f.emergencia_telefono)) return 'El teléfono de emergencia debe tener al menos 10 dígitos.';
  if (paso.clave === 'banco' && lleno(f.clabe) && !clabeValida(f.clabe)) return 'La CLABE debe tener 18 dígitos y ser válida (revisa el último dígito).';
  if (paso.clave === 'nacimiento' && lleno(f.fecha_nacimiento)) {
    const y = Number(String(f.fecha_nacimiento).slice(0, 4));
    const hoy = new Date().getFullYear();
    if (!(y > hoy - 100 && y <= hoy - 15)) return 'Revisa la fecha de nacimiento.';
  }
  return '';
};
export const pasosObligatorios = PASOS.filter((p) => p.obligatorio);
export const obligatoriosFaltantes = (ficha, agente) => pasosObligatorios.filter((p) => !pasoCompleto(p, ficha, agente));
export const pasosCompletados = (ficha, agente) => PASOS.filter((p) => p.clave !== 'revision' && pasoCompleto(p, ficha, agente)).length; // de 12 capturables
export const fichaCompleta = (agente, ficha) => !!((agente && agente.ficha_completa_en) || (ficha && ficha.completada_en));

// Estado para la tarjeta del admin: { clave, texto }
export const estadoFicha = (agente, ficha, conConteo = true) => {
  if (fichaCompleta(agente, ficha)) return { clave: 'completa', texto: `Completa ${fmtFechaCorta(agente.ficha_completa_en || ficha.completada_en)}` };
  if (ficha && pasosCompletados(ficha, agente) > 0) return { clave: 'incompleta', texto: conConteo ? `Incompleta (${pasosCompletados(ficha, agente)}/${TOTAL_PASOS})` : 'Incompleta' };
  if (agente && agente.ficha_solicitada_en) return { clave: 'solicitada', texto: `Solicitada ${fmtFechaCorta(agente.ficha_solicitada_en)}` };
  return { clave: 'sin', texto: 'Sin solicitar' };
};
export const fmtFechaCorta = (f) => {
  if (!f) return '';
  try { return new Date(f).toLocaleDateString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', year: 'numeric' }); } catch { return String(f); }
};

// ── Lecturas / escrituras ──
export const cargarFicha = async (agenteId) => {
  const { data, error } = await supabase.from('agentes_ficha').select('*').eq('agente_id', agenteId).maybeSingle();
  if (error) throw error;
  return data || null;
};
// Fichas de varios agentes (lado admin). Si no puede ver sensibles, NO se piden esas columnas.
export const cargarFichas = async (ids, puedeVerSensibles) => {
  if (!ids.length) return {};
  const cols = puedeVerSensibles ? '*' : 'agente_id,nombre_completo,fecha_nacimiento,celular,correo_personal,direccion,emergencia_nombre,emergencia_telefono,emergencia_parentesco,talla_playera,notas,completada_en,actualizado_en,actualizado_por';
  const { data, error } = await supabase.from('agentes_ficha').select(cols).in('agente_id', ids);
  if (error) throw error;
  return Object.fromEntries((data || []).map((f) => [f.agente_id, f]));
};
export const guardarPasoFicha = async (agenteId, patch, quien) => {
  const fila = { agente_id: agenteId, ...patch, actualizado_en: new Date().toISOString(), actualizado_por: quien || null };
  const { data, error } = await supabase.from('agentes_ficha').upsert(fila, { onConflict: 'agente_id' }).select().single();
  if (error) throw error;
  return data;
};
export const enviarFicha = async (agenteId, quien) => {
  const ahora = new Date().toISOString();
  const f = await guardarPasoFicha(agenteId, { completada_en: ahora }, quien);
  const { error } = await supabase.from('agentes').update({ ficha_completa_en: ahora }).eq('id', agenteId);
  if (error) throw error;
  return { ficha: f, ficha_completa_en: ahora };
};
export const solicitarFicha = async (agenteId, quien) => {
  const ahora = new Date().toISOString();
  const { error } = await supabase.from('agentes').update({ ficha_solicitada_en: ahora, ficha_solicitada_por: quien || null }).eq('id', agenteId);
  if (error) throw error;
  return ahora;
};
export const guardarPreferencias = async (agenteId, preferencias) => {
  const { error } = await supabase.from('agentes').update({ preferencias }).eq('id', agenteId);
  if (error) throw error;
};

// ── Archivos (bucket privado) ──
const extDe = (file) => {
  const porNombre = String(file.name || '').split('.').pop().toLowerCase();
  if (/^(jpe?g|png|webp|heic|pdf)$/.test(porNombre)) return porNombre === 'jpeg' ? 'jpg' : porNombre;
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type === 'image/png') return 'png';
  return 'jpg';
};
export const MAX_ARCHIVO = 8 * 1024 * 1024;
export const validarArchivo = (file, { pdf = false } = {}) => {
  if (!file) return 'Elige un archivo.';
  if (file.size > MAX_ARCHIVO) return 'El archivo pesa más de 8 MB.';
  const esImg = /^image\//.test(file.type);
  const esPdf = file.type === 'application/pdf';
  if (!esImg && !(pdf && esPdf)) return pdf ? 'Solo imágenes o PDF.' : 'Solo imágenes.';
  return '';
};
// Sube y devuelve la RUTA dentro del bucket (<agente_id>/<campo>.<ext>). upsert: corregir reemplaza el anterior.
export const subirArchivoFicha = async (agenteId, campo, file) => {
  const ruta = `${agenteId}/${campo}.${extDe(file)}`;
  const { error } = await supabase.storage.from(BUCKET_FICHAS).upload(ruta, file, { upsert: true, contentType: file.type || undefined });
  if (error) throw error;
  return ruta;
};
// URL firmada (segundos). Si lo guardado es una URL completa (legado), se devuelve tal cual.
export const urlFirmada = async (ruta, segundos = 600) => {
  if (!ruta) return null;
  if (/^https?:\/\//.test(ruta)) return ruta;
  const { data, error } = await supabase.storage.from(BUCKET_FICHAS).createSignedUrl(ruta, segundos);
  if (error) throw error;
  return data && data.signedUrl;
};
// ── Bloque 29: solo lectura, reapertura y versiones ──
// Pendiente = hay solicitud y no hay envío posterior a ella (la RPC ficha_reabrir vuelve a marcar ficha_solicitada_en).
export const fichaPendienteDe = (agente) => {
  if (!agente || !agente.ficha_solicitada_en) return false;
  if (!agente.ficha_completa_en) return true;
  return new Date(agente.ficha_solicitada_en).getTime() > new Date(agente.ficha_completa_en).getTime();
};
export const enmascararClabe = (clabe) => { const d = soloDigitos(clabe); return d ? `**** **** **** ${d.slice(-4)}` : ''; };
// Reabrir para edición: SOLO la RPC. La base guarda la versión anterior en agentes_ficha_versiones y sube version.
export const reabrirFicha = async (agenteId, admin, motivo) => {
  const { data, error } = await supabase.rpc('ficha_reabrir', { p_agente_id: agenteId, p_admin: admin || null, p_motivo: motivo || null });
  if (error) throw error;
  return data; // nueva versión
};
export const cargarVersiones = async (agenteId) => {
  const { data, error } = await supabase.from('agentes_ficha_versiones').select('agente_id,version,datos,guardada_en,reabierta_por,reabierta_motivo').eq('agente_id', agenteId).order('version', { ascending: true });
  if (error) throw error;
  return data || [];
};
// Campos que se comparan entre versiones (sin metadatos).
export const CAMPOS_COMPARABLES = ['nombre_completo', 'fecha_nacimiento', 'celular', 'correo_personal', 'direccion', 'emergencia_nombre', 'emergencia_telefono', 'emergencia_parentesco', 'identificacion_tipo', 'identificacion_url', 'comprobante_domicilio_url', 'banco', 'clabe', 'titular_cuenta', 'caratula_bancaria_url', 'talla_playera', 'notas'];
const normal = (v) => (v == null ? '' : String(v).trim());
export const diferencias = (antes, despues, campos = CAMPOS_COMPARABLES) => campos.filter((c) => normal(antes && antes[c]) !== normal(despues && despues[c])).map((c) => ({ campo: c, antes: antes && antes[c], despues: despues && despues[c] }));

export const guardarFotoPerfil = async (agenteId, file) => {
  const ruta = await subirArchivoFicha(agenteId, 'foto', file);
  const { error } = await supabase.from('agentes').update({ foto_url: ruta }).eq('id', agenteId);
  if (error) throw error;
  return ruta;
};
